import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  repositorioSensores,
  type PresenciaNodo,
} from '../../../data/repositories/RepositorioSensores';
import type { Sensor } from '../../../domain/entities/Sensor';
import type { EstadoSensor } from '../../../domain/value-objects/EstadoSensor';
import type { EstadoSensorValor } from '../../../domain/value-objects/EstadoSensor';
import type { DominioValor } from '../../../domain/value-objects/Dominio';
import { useCascadaFiltros } from '../../hooks/useCascadaFiltros';
import { useTiempoReal } from '../../hooks/useTiempoReal';
import { STORAGE_PREFIX } from '../../../config/env';

/** Cómo se agrupan los sensores en pantalla. */
export type ModoVista = 'categoria' | 'sensor' | 'nodo' | 'area';

export const MODOS_VISTA: { valor: ModoVista; etiqueta: string }[] = [
  { valor: 'categoria', etiqueta: 'Por categoría' },
  { valor: 'sensor', etiqueta: 'Por sensor' },
  { valor: 'nodo', etiqueta: 'Por nodo' },
  { valor: 'area', etiqueta: 'Por ubicación' },
];

const CLAVE_VISTA = `${STORAGE_PREFIX}modo-vista-monitoreo`;

/**
 * Cuánto tiempo un sensor que acaba de cambiar se queda arriba y resaltado.
 *
 * Es un compromiso: demasiado corto y el cambio pasa desapercibido en una
 * instalación con muchos sensores; demasiado largo y la lista no se queda
 * quieta nunca, que es peor para trabajar.
 */
const VENTANA_CAMBIO_MS = 45_000;

/*
 * Mapa vacío compartido, para cuando la consulta de presencia todavía no ha
 * respondido. Crear uno nuevo en cada render haría que todo lo que dependa de
 * él —el estado de cada sensor, el filtrado, los conteos— se recalculara sin
 * que hubiera cambiado nada.
 */
const SIN_PRESENCIA: ReadonlyMap<number, PresenciaNodo> = new Map();

/** Huella de la lectura de un sensor, para saber si cambió entre dos cargas. */
function huellaDeLectura(sensor: Sensor): string {
  return `${sensor.lectura?.valor ?? ''}|${sensor.lectura?.timestamp?.getTime() ?? ''}|${sensor.estado.valor}`;
}

/**
 * Qué parte de la ubicación tiene sentido escribir en cada tarjeta.
 *
 * Repetir en las veintiocho tarjetas un dato que ya está en el encabezado del
 * grupo, o que el usuario acaba de elegir en los filtros, no informa de nada y
 * roba el sitio a lo que sí falta.
 */
export interface ContextoVisible {
  cliente: boolean;
  area: boolean;
  nodo: boolean;
}

/** Un grupo de sensores con su encabezado. */
export interface GrupoSensores {
  clave: string;
  titulo: string;
  /** Identificador del nodo, para poder mostrar su conexión. */
  nodoId: number | null;
  sensores: Sensor[];
}

function leerModoGuardado(): ModoVista {
  const guardado = localStorage.getItem(CLAVE_VISTA);
  return MODOS_VISTA.some((m) => m.valor === guardado) ? (guardado as ModoVista) : 'categoria';
}

/**
 * Estado y datos de la pantalla de monitoreo.
 *
 * Los sensores se piden una vez y el agrupado, el filtrado y la búsqueda se
 * resuelven en memoria: cambiar de vista no debe suponer otra consulta.
 */
export function useMonitoreo() {
  const clienteConsultas = useQueryClient();
  const cascada = useCascadaFiltros({}, 'monitoreo');

  const [modo, setModo] = useState<ModoVista>(leerModoGuardado);
  const [busqueda, setBusqueda] = useState('');
  const [categoria, setCategoria] = useState<DominioValor | null>(null);
  const [estados, setEstados] = useState<EstadoSensorValor[]>([]);
  const [tipo, setTipo] = useState<'analogico' | 'digital' | null>(null);

  const filtrosConsulta = useMemo(
    () => ({
      clienteId: cascada.seleccion.clienteId,
      areaId: cascada.seleccion.areaId,
      nodoId: cascada.seleccion.nodoId,
    }),
    [cascada.seleccion],
  );

  const consulta = useQuery({
    queryKey: ['sensores', filtrosConsulta],
    queryFn: () => repositorioSensores.listarConValores(filtrosConsulta),
  });

  const sensores = useMemo(() => consulta.data ?? [], [consulta.data]);

  /*
   * Qué sensores han cambiado hace poco, para subirlos al principio.
   *
   * El servidor no dice «este cambió»: manda un aviso y la pantalla vuelve a
   * pedir la lista entera. Así que se compara con lo que había. La primera
   * carga no marca nada —si no, entrar en la pantalla dejaría todo resaltado y
   * el resalte no significaría nada.
   */
  const huellas = useRef<Map<number, string> | null>(null);
  const [cambiados, setCambiados] = useState<ReadonlyMap<number, number>>(new Map());

  useEffect(() => {
    if (sensores.length === 0) return;

    const ahora = Date.now();
    const previas = huellas.current;
    const nuevas = new Map<number, string>();
    const recienCambiados: number[] = [];

    for (const s of sensores) {
      const huella = huellaDeLectura(s);
      nuevas.set(s.id, huella);

      const anterior = previas?.get(s.id);
      if (anterior !== undefined && anterior !== huella) recienCambiados.push(s.id);
    }

    huellas.current = nuevas;
    if (recienCambiados.length === 0) return;

    setCambiados((previos) => {
      const copia = new Map(previos);
      for (const id of recienCambiados) copia.set(id, ahora);
      return copia;
    });
  }, [sensores]);

  // Los cambios caducan solos: si no, la lista quedaría ordenada para siempre
  // por algo que pasó hace media hora.
  useEffect(() => {
    if (cambiados.size === 0) return;

    const temporizador = window.setInterval(() => {
      const limite = Date.now() - VENTANA_CAMBIO_MS;
      setCambiados((previos) => {
        const vigentes = new Map([...previos].filter(([, momento]) => momento > limite));
        return vigentes.size === previos.size ? previos : vigentes;
      });
    }, 5_000);

    return () => window.clearInterval(temporizador);
  }, [cambiados.size]);

  const nodoIds = useMemo(
    () => [...new Set(sensores.map((s) => s.nodoId).filter((id): id is number => id !== null))],
    [sensores],
  );

  const presencia = useQuery({
    queryKey: ['presencia-nodos', nodoIds],
    queryFn: () => repositorioSensores.presenciaDeNodos(nodoIds),
    enabled: nodoIds.length > 0,
    // La conexión cambia sola con el latido; se refresca sin esperar a que el
    // usuario haga nada, pero sin llegar a ser un sondeo agresivo.
    refetchInterval: 30_000,
  });

  const mapaPresencia = presencia.data ?? SIN_PRESENCIA;

  /*
   * Estado de un sensor con el latido de su nodo incluido.
   *
   * El sensor por sí solo no sabe si su nodo responde —esa información llega
   * de otra consulta, que se refresca por su cuenta—, así que se la damos aquí
   * y es este estado, y no `sensor.estado`, el que manda en toda la pantalla:
   * color, filtros, conteos y orden.
   */
  const estadoDe = useCallback(
    (sensor: Sensor): EstadoSensor =>
      sensor.estadoCon(
        sensor.nodoId !== null ? (mapaPresencia.get(sensor.nodoId)?.enLinea ?? null) : null,
      ),
    [mapaPresencia],
  );

  const refrescar = useCallback(() => {
    void clienteConsultas.invalidateQueries({ queryKey: ['sensores'] });
    void clienteConsultas.invalidateQueries({ queryKey: ['presencia-nodos'] });
  }, [clienteConsultas]);

  // Cada lectura nueva vuelve a pedir la vista, que es la que trae el valor ya
  // acompañado de sus umbrales y de los nombres de nodo y área.
  useTiempoReal({ tabla: 'last_sensor_values', evento: '*' }, refrescar);
  useTiempoReal({ tabla: 'sensores_runtime', evento: 'UPDATE' }, refrescar);

  const cambiarModo = useCallback((nuevo: ModoVista) => {
    setModo(nuevo);
    localStorage.setItem(CLAVE_VISTA, nuevo);
  }, []);

  const alternarEstado = useCallback((valor: EstadoSensorValor) => {
    setEstados((previos) =>
      previos.includes(valor) ? previos.filter((e) => e !== valor) : [...previos, valor],
    );
  }, []);

  /** Sensores que pasan todos los filtros que no viajan al servidor. */
  const filtrados = useMemo(() => {
    return sensores.filter((s) => {
      if (!s.coincideCon(busqueda)) return false;
      if (categoria && s.dominio.valor !== categoria) return false;
      if (tipo === 'analogico' && s.esDigital) return false;
      if (tipo === 'digital' && !s.esDigital) return false;
      if (estados.length > 0 && !estados.includes(estadoDe(s).valor)) return false;
      return true;
    });
  }, [sensores, busqueda, categoria, tipo, estados, estadoDe]);

  /*
   * Orden dentro de cada grupo. Lo que acaba de cambiar va primero, y entre
   * varios cambios manda el más reciente: es lo que se está mirando. Después
   * los que piden atención, y el resto por nombre para que la lista sea
   * predecible cuando no pasa nada.
   */
  const ordenar = useCallback(
    (lista: Sensor[]): Sensor[] =>
      [...lista].sort((a, b) => {
        const cambioA = cambiados.get(a.id) ?? 0;
        const cambioB = cambiados.get(b.id) ?? 0;
        if (cambioA !== cambioB) return cambioB - cambioA;

        const urgenciaA = estadoDe(a).urgencia;
        const urgenciaB = estadoDe(b).urgencia;
        if (urgenciaA !== urgenciaB) return urgenciaB - urgenciaA;

        return a.nombre.localeCompare(b.nombre, 'es');
      }),
    [cambiados, estadoDe],
  );

  /** Agrupa según el modo elegido, ordenando los grupos por nombre. */
  const grupos = useMemo<GrupoSensores[]>(() => {
    if (modo === 'sensor') {
      return [
        {
          clave: 'todos',
          titulo: 'Todos los sensores',
          nodoId: null,
          sensores: ordenar(filtrados),
        },
      ];
    }

    const mapa = new Map<string, GrupoSensores>();

    for (const s of filtrados) {
      let clave: string;
      let titulo: string;
      let nodoId: number | null = null;

      if (modo === 'categoria') {
        clave = s.dominio.valor;
        titulo = s.dominio.etiqueta;
      } else if (modo === 'nodo') {
        clave = `nodo-${s.nodoId ?? 'sin'}`;
        titulo = s.nodoNombre ?? 'Sin nodo asignado';
        nodoId = s.nodoId;
      } else {
        clave = `area-${s.areaId ?? 'sin'}`;
        titulo = s.areaNombre ?? 'Sin área asignada';
      }

      const grupo = mapa.get(clave);
      if (grupo) grupo.sensores.push(s);
      else mapa.set(clave, { clave, titulo, nodoId, sensores: [s] });
    }

    return [...mapa.values()]
      .map((g) => ({ ...g, sensores: ordenar(g.sensores) }))
      .sort((a, b) => a.titulo.localeCompare(b.titulo, 'es'));
  }, [filtrados, modo, ordenar]);

  /*
   * Se oculta lo que el propio contexto ya dice: el agrupado (por nodo, por
   * ubicación) y los filtros de la cascada. Agrupar por categoría no dice nada
   * de dónde está el sensor, así que ahí se escribe todo.
   */
  const contextoVisible = useMemo<ContextoVisible>(
    () => ({
      cliente: cascada.seleccion.clienteId === null,
      area: cascada.seleccion.areaId === null && modo !== 'area',
      nodo: cascada.seleccion.nodoId === null && modo !== 'nodo',
    }),
    [cascada.seleccion, modo],
  );

  /** Cuántos sensores hay en cada estado, para los accesos rápidos. */
  const conteoEstados = useMemo(() => {
    const conteo: Record<string, number> = {};
    for (const s of sensores) {
      const valor = estadoDe(s).valor;
      conteo[valor] = (conteo[valor] ?? 0) + 1;
    }
    return conteo;
  }, [sensores, estadoDe]);

  const limpiarFiltros = useCallback(() => {
    cascada.limpiar();
    setBusqueda('');
    setCategoria(null);
    setEstados([]);
    setTipo(null);
  }, [cascada]);

  return {
    cascada,
    grupos,
    totalSensores: sensores.length,
    totalFiltrados: filtrados.length,
    conteoEstados,
    presencia: mapaPresencia,
    contextoVisible,
    /** Estado de un sensor con el latido de su nodo ya tenido en cuenta. */
    estadoDe,
    /** Identificadores de los sensores que cambiaron hace poco. */
    cambiados,

    cargando: consulta.isLoading,
    error: consulta.isError,
    recargando: consulta.isFetching,

    modo,
    busqueda,
    categoria,
    estados,
    tipo,

    cambiarModo,
    setBusqueda,
    setCategoria,
    alternarEstado,
    setTipo,
    limpiarFiltros,
    refrescar,
  };
}
